<template>
  <div class="min-h-screen bg-white">
    <!-- 헤더 -->
    <AppHeader title="파트너스센터" backTo="/partners/intro" />

    <!-- 메인 콘텐츠 -->
    <main class="max-w-lg mx-auto px-5 pt-6">
      <!-- 상단 안내 -->
      <section class="mb-8">
        <h1 class="text-[26px] font-black text-slate-900 leading-tight tracking-tight mb-3">
          파트너스센터에<br/>오신 것을 환영해요
        </h1>
        <p class="text-[15px] font-medium text-slate-500">
          {{ activeTab === 'login' ? '로그인하고 고객을 만나보세요' : '회원가입 후 파트너로 등록하세요' }}
        </p>
      </section>

      <!-- 탭 전환 -->
      <div class="flex bg-slate-100 rounded-xl p-1 mb-6">
        <button
          @click="activeTab = 'login'"
          :class="[
            'flex-1 py-3 text-[15px] font-bold rounded-lg transition-all',
            activeTab === 'login'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500'
          ]"
        >
          로그인
        </button>
        <button
          @click="activeTab = 'signup'"
          :class="[
            'flex-1 py-3 text-[15px] font-bold rounded-lg transition-all',
            activeTab === 'signup'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500'
          ]"
        >
          회원가입
        </button>
      </div>

      <!-- 소셜 로그인 -->
      <section class="mb-6">
        <button
          @click="handleGoogleLogin"
          :disabled="isLoading"
          class="w-full py-4 border border-slate-200 rounded-2xl flex items-center justify-center gap-3 active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          <svg class="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span class="text-[15px] font-bold text-slate-700">Google로 {{ activeTab === 'login' ? '계속하기' : '가입하기' }}</span>
        </button>
      </section>

      <!-- 구분선 -->
      <div class="relative mb-6">
        <div class="absolute inset-0 flex items-center">
          <div class="w-full border-t border-slate-100"></div>
        </div>
        <div class="relative flex justify-center">
          <span class="px-4 bg-white text-[13px] font-medium text-slate-400">
            {{ activeTab === 'login' ? '또는 이메일로 로그인' : '또는 이메일로 가입' }}
          </span>
        </div>
      </div>

      <!-- 로그인 폼 -->
      <form v-if="activeTab === 'login'" @submit.prevent="handleLogin" class="space-y-4">
        <!-- 이메일 -->
        <div>
          <label class="block text-[13px] font-bold text-slate-700 mb-2">이메일</label>
          <input
            v-model="email"
            type="email"
            placeholder="example@email.com"
            class="w-full px-4 py-3.5 rounded-xl border border-slate-200 text-[15px] font-medium text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
            required
          />
        </div>

        <!-- 비밀번호 -->
        <div>
          <label class="block text-[13px] font-bold text-slate-700 mb-2">비밀번호</label>
          <input
            v-model="password"
            type="password"
            placeholder="비밀번호 입력"
            class="w-full px-4 py-3.5 rounded-xl border border-slate-200 text-[15px] font-medium text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
            required
          />
        </div>

        <!-- 에러 메시지 -->
        <div v-if="errorMessage" class="py-3 px-4 bg-red-50 rounded-xl">
          <p class="text-[14px] font-semibold text-red-500 text-center">
            {{ errorMessage }}
          </p>
        </div>

        <!-- 로그인 버튼 -->
        <button
          type="submit"
          :disabled="isLoading"
          class="w-full py-4 bg-blue-600 text-white text-[16px] font-bold rounded-2xl active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          <span v-if="isLoading">로그인 중...</span>
          <span v-else>로그인</span>
        </button>
      </form>

      <!-- 회원가입 폼 -->
      <form v-else @submit.prevent="handleSignup" class="space-y-4">
        <!-- 이메일 -->
        <div>
          <label class="block text-[13px] font-bold text-slate-700 mb-2">이메일</label>
          <input
            v-model="email"
            type="email"
            placeholder="example@email.com"
            class="w-full px-4 py-3.5 rounded-xl border border-slate-200 text-[15px] font-medium text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
            required
          />
        </div>

        <!-- 비밀번호 -->
        <div>
          <label class="block text-[13px] font-bold text-slate-700 mb-2">비밀번호</label>
          <input
            v-model="password"
            type="password"
            placeholder="6자 이상 입력"
            class="w-full px-4 py-3.5 rounded-xl border border-slate-200 text-[15px] font-medium text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
            required
            minlength="6"
          />
        </div>

        <!-- 비밀번호 확인 -->
        <div>
          <label class="block text-[13px] font-bold text-slate-700 mb-2">비밀번호 확인</label>
          <input
            v-model="passwordConfirm"
            type="password"
            placeholder="비밀번호 재입력"
            class="w-full px-4 py-3.5 rounded-xl border border-slate-200 text-[15px] font-medium text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
            required
            minlength="6"
          />
          <p v-if="passwordConfirm && password !== passwordConfirm" class="text-[13px] text-red-500 mt-1">
            비밀번호가 일치하지 않습니다
          </p>
        </div>

        <!-- 에러 메시지 -->
        <div v-if="errorMessage" class="py-3 px-4 bg-red-50 rounded-xl">
          <p class="text-[14px] font-semibold text-red-500 text-center">
            {{ errorMessage }}
          </p>
        </div>

        <!-- 회원가입 버튼 -->
        <button
          type="submit"
          :disabled="isLoading || (passwordConfirm && password !== passwordConfirm)"
          class="w-full py-4 bg-blue-600 text-white text-[16px] font-bold rounded-2xl active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          <span v-if="isLoading">가입 중...</span>
          <span v-else>회원가입</span>
        </button>

        <!-- 가입 안내 -->
        <p class="text-[12px] text-slate-400 text-center">
          회원가입 시 <span class="text-blue-600 font-medium">이용약관</span> 및
          <span class="text-blue-600 font-medium">개인정보처리방침</span>에 동의하게 됩니다.
        </p>
      </form>

      <!-- 파트너 등록 안내 (로그인 탭일 때만) -->
      <section v-if="activeTab === 'login'" class="mt-10 pt-8 border-t border-slate-100">
        <div class="text-center">
          <p class="text-[14px] font-medium text-slate-500 mb-4">
            아직 파트너가 아니신가요?
          </p>
          <router-link
            to="/partners/register"
            class="inline-flex items-center gap-1 text-[15px] font-bold text-blue-600"
          >
            파트너 등록 신청하기
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </router-link>
        </div>
      </section>

      <!-- 하단 링크 -->
      <section class="mt-8 pb-10">
        <div class="flex justify-center gap-6">
          <router-link
            to="/"
            class="text-[13px] font-medium text-slate-400 active:text-slate-600"
          >
            메인으로
          </router-link>
          <router-link
            to="/login"
            class="text-[13px] font-medium text-slate-400 active:text-slate-600"
          >
            일반 로그인
          </router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuth } from '@/composables/common/useAuth'
import AppHeader from '@/components/common/AppHeader.vue'

export default {
  name: 'PartnersLoginView',
  components: {
    AppHeader
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const { login, loginWithGoogle, register, userProfile } = useAuth()

    const activeTab = ref('login')
    const email = ref('')
    const password = ref('')
    const passwordConfirm = ref('')
    const isLoading = ref(false)
    const errorMessage = ref('')

    // URL 파라미터로 탭 전환 (signup 탭으로 바로 이동)
    onMounted(() => {
      if (route.query.tab === 'signup') {
        activeTab.value = 'signup'
      }
    })

    const checkRoleAndRedirect = async () => {
      // 약간의 딜레이 후 프로필 체크 (Firebase 동기화 대기)
      await new Promise(resolve => setTimeout(resolve, 500))

      if (userProfile.value) {
        if (userProfile.value.role === 'partner' || userProfile.value.role === 'admin') {
          // 파트너 또는 관리자 → 대시보드
          router.push('/partners')
        } else {
          // 일반 사용자 → 등록 페이지
          router.push('/partners/register')
        }
      } else {
        // 프로필 없음 → 등록 페이지
        router.push('/partners/register')
      }
    }

    const handleGoogleLogin = async () => {
      errorMessage.value = ''
      isLoading.value = true

      const result = await loginWithGoogle()

      if (result.success) {
        await checkRoleAndRedirect()
      } else {
        errorMessage.value = result.error
      }

      isLoading.value = false
    }

    const handleLogin = async () => {
      errorMessage.value = ''
      isLoading.value = true

      const result = await login(email.value, password.value)

      if (result.success) {
        await checkRoleAndRedirect()
      } else {
        errorMessage.value = result.error
      }

      isLoading.value = false
    }

    const handleSignup = async () => {
      errorMessage.value = ''

      // 비밀번호 확인
      if (password.value !== passwordConfirm.value) {
        errorMessage.value = '비밀번호가 일치하지 않습니다.'
        return
      }

      // 비밀번호 길이
      if (password.value.length < 6) {
        errorMessage.value = '비밀번호는 6자 이상이어야 합니다.'
        return
      }

      isLoading.value = true

      const result = await register(email.value, password.value)

      if (result.success) {
        // 회원가입 성공 → 파트너 등록 페이지로
        router.push('/partners/register')
      } else {
        errorMessage.value = result.error
      }

      isLoading.value = false
    }

    return {
      activeTab,
      email,
      password,
      passwordConfirm,
      isLoading,
      errorMessage,
      handleLogin,
      handleSignup,
      handleGoogleLogin
    }
  }
}
</script>
